import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ThemeToggle } from "@/components/ThemeToggle";
import {
  Zap, QrCode, RefreshCw, ShieldCheck, Smartphone, Webhook, Receipt,
  TrendingUp, Layers, ArrowRight, CheckCircle2, Building2, IndianRupee,
} from "lucide-react";

const Step = ({ n, icon: Icon, title, desc }) => (
  <div className="relative flex gap-4">
    <div className="flex flex-col items-center">
      <div className="h-10 w-10 shrink-0 rounded-xl bg-primary/10 text-primary grid place-items-center font-heading font-bold">
        {n}
      </div>
      {n !== 6 && <div className="w-px flex-1 bg-border mt-2" />}
    </div>
    <div className="pb-8">
      <div className="flex items-center gap-2">
        <Icon className="h-4 w-4 text-primary" />
        <h4 className="font-heading font-semibold">{title}</h4>
      </div>
      <p className="mt-1 text-sm text-muted-foreground max-w-xl">{desc}</p>
    </div>
  </div>
);

export default function Landing() {
  return (
    <div className="min-h-screen bg-background grain-overlay">
      <header className="sticky top-0 z-30 border-b bg-card/60 glass">
        <div className="mx-auto max-w-6xl flex items-center justify-between px-4 md:px-6 py-3">
          <div className="flex items-center gap-2.5">
            <div className="h-9 w-9 rounded-xl bg-primary grid place-items-center shadow-lg shadow-primary/30">
              <Zap className="h-5 w-5 text-primary-foreground" fill="currentColor" />
            </div>
            <span className="font-heading font-extrabold text-xl tracking-tight">WoltPe</span>
          </div>
          <div className="flex items-center gap-2">
            <ThemeToggle />
            <Link to="/login"><Button data-testid="header-login-button">Sign in <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
          </div>
        </div>
      </header>

      {/* Hero */}
      <section className="mx-auto max-w-6xl px-4 md:px-6 pt-16 pb-12">
        <div className="animate-fade-up max-w-3xl">
          <Badge variant="outline" className="border-primary/30 bg-primary/5 text-primary mb-5">
            0% Gateway MDR · Direct UPI Settlement
          </Badge>
          <h1 className="font-heading text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight leading-[1.05]">
            Collect UPI payments <span className="text-primary">without a payment gateway.</span>
          </h1>
          <p className="mt-5 text-base md:text-lg text-muted-foreground max-w-2xl">
            A white-label SaaS where merchants collect directly into their own UPI IDs (Paytm, PhonePe,
            BharatPe, bank QR). Payments auto-reconcile via bank-SMS webhooks and unique-paise matching —
            no PSP, no MDR. Run your own distributor network and earn commission on every rupee.
          </p>
          <div className="mt-7 flex flex-wrap gap-3">
            <Link to="/login"><Button size="lg" data-testid="hero-get-started">Open the panel <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
            <a href="#how"><Button size="lg" variant="outline">See how it works</Button></a>
          </div>
          <div className="mt-6 flex flex-wrap gap-x-6 gap-y-2 text-sm text-muted-foreground">
            {["No MDR / gateway fees", "VPA rotation & load balancing", "Instant auto-reconciliation"].map((t) => (
              <span key={t} className="flex items-center gap-1.5"><CheckCircle2 className="h-4 w-4 text-success" />{t}</span>
            ))}
          </div>
        </div>
      </section>

      {/* Role cards */}
      <section className="mx-auto max-w-6xl px-4 md:px-6 py-8">
        <div className="grid gap-4 md:grid-cols-3">
          {[
            { icon: Building2, t: "Super Admin", d: "Set the platform fee %, manage the full distributor & merchant network, and watch global volume and revenue in real time.", c: "text-primary bg-primary/10" },
            { icon: Layers, t: "Distributor", d: "Onboard your downline merchants and earn a fixed share of the platform fee on every collection they make.", c: "text-chart-2 bg-chart-2/10" },
            { icon: Receipt, t: "Merchant", d: "Add multiple UPI IDs, auto-rotate them, generate dynamic QR + intent links, and see net settlement instantly.", c: "text-success bg-success/10" },
          ].map((r) => (
            <Card key={r.t} className="p-6 hover:border-primary/40 transition-colors">
              <div className={`h-11 w-11 rounded-xl grid place-items-center ${r.c}`}><r.icon className="h-5 w-5" /></div>
              <h3 className="mt-4 font-heading text-lg font-semibold">{r.t}</h3>
              <p className="mt-1.5 text-sm text-muted-foreground">{r.d}</p>
            </Card>
          ))}
        </div>
      </section>

      {/* How it works */}
      <section id="how" className="mx-auto max-w-6xl px-4 md:px-6 py-14">
        <div className="max-w-2xl mb-10">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
            How we confirm payments without a gateway
          </h2>
          <p className="mt-2 text-muted-foreground">
            The hard part is knowing if a payment succeeded with no PSP callback. WoltPe solves this with
            three layers working together.
          </p>
        </div>

        <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr]">
          <div>
            <Step n={1} icon={QrCode} title="Merchant registers UPI IDs" desc="Business VPAs on Paytm / PhonePe / BharatPe or a bank QR are added to a rotation pool, each with optional daily limits." />
            <Step n={2} icon={RefreshCw} title="VPA rotation on checkout" desc="Each incoming collection is assigned to the least-recently-used active VPA under its daily cap — spreading volume to avoid flags." />
            <Step n={3} icon={IndianRupee} title="Exact-amount collection" desc="The customer pays the exact amount — no paise suffix added — so there's never any confusion at checkout." />
            <Step n={4} icon={Smartphone} title="UPI intent + dynamic QR" desc="A upi://pay deep-link and QR launch GPay, PhonePe, Paytm or BHIM directly — the customer pays peer-to-peer into the merchant VPA." />
            <Step n={5} icon={Webhook} title="Bank-SMS webhook listener" desc="A phone holding the bank account forwards credit SMS to our webhook; we parse amount + UTR and auto-mark the matching order SUCCESS." />
            <Step n={6} icon={ShieldCheck} title="UTR reconciliation" desc="The payer submits the 12-digit UTR and the merchant confirms — every rupee is reconciled against the exact amount." />
          </div>

          <div className="space-y-4">
            <Card className="p-6 border-primary/30 bg-primary/[0.03]">
              <TrendingUp className="h-5 w-5 text-primary" />
              <h3 className="mt-3 font-heading font-semibold">Hierarchical commission ledger</h3>
              <p className="mt-1 text-sm text-muted-foreground">On every successful collection:</p>
              <ul className="mt-3 space-y-2 text-sm">
                <li className="flex justify-between border-b border-dashed pb-2"><span className="text-muted-foreground">Gross collected</span><span className="font-mono">₹499.37</span></li>
                <li className="flex justify-between border-b border-dashed pb-2"><span className="text-muted-foreground">Platform fee (2%)</span><span className="font-mono text-warning">₹9.99</span></li>
                <li className="flex justify-between border-b border-dashed pb-2"><span className="text-muted-foreground">Distributor share (30%)</span><span className="font-mono text-chart-2">₹3.00</span></li>
                <li className="flex justify-between border-b border-dashed pb-2"><span className="text-muted-foreground">Super admin revenue</span><span className="font-mono text-primary">₹6.99</span></li>
                <li className="flex justify-between pt-1"><span className="font-medium">Merchant net settlement</span><span className="font-mono text-success font-semibold">₹489.38</span></li>
              </ul>
            </Card>
            <Card className="p-6">
              <ShieldCheck className="h-5 w-5 text-success" />
              <h3 className="mt-3 font-heading font-semibold">Why it's cheaper</h3>
              <p className="mt-1 text-sm text-muted-foreground">
                Money moves peer-to-peer over UPI rails directly into the merchant's own bank account —
                there's no aggregator in the flow, so there's no 1.8–2% MDR. You only charge your own
                platform fee, set from the Super Admin panel.
              </p>
            </Card>
          </div>
        </div>
      </section>

      <section className="mx-auto max-w-6xl px-4 md:px-6 pb-20">
        <Card className="p-8 md:p-12 text-center bg-gradient-to-b from-primary/[0.06] to-transparent border-primary/20">
          <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">Ready to run your own UPI network?</h2>
          <p className="mt-2 text-muted-foreground max-w-xl mx-auto">Sign in with a demo account to explore the Super Admin, Distributor and Merchant panels end-to-end.</p>
          <Link to="/login"><Button size="lg" className="mt-6" data-testid="cta-login-button">Launch the panel <ArrowRight className="ml-1.5 h-4 w-4" /></Button></Link>
        </Card>
      </section>

      <footer className="border-t py-6">
        <div className="mx-auto max-w-6xl px-4 md:px-6 flex items-center justify-between text-sm text-muted-foreground">
          <span>© 2026 WoltPe · UPI Collection SaaS</span>
          <span className="font-mono text-xs">TLS encrypted</span>
        </div>
      </footer>
    </div>
  );
}
